import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import { submitReview } from '../services/api';

/**
 * QuickReview — inline 1-tap star rating for completed deliveries.
 *
 * Click a star → rating is submitted immediately (no dialog, no comment
 * required). The star track shows a hover preview. After submit the row
 * flips to a compact "Your review" summary with a subtle "Add a comment"
 * link that opens the existing full-review dialog if the customer wants
 * to elaborate.
 *
 * Backwards-compatible: this uses the same `POST /deliveries/{id}/review`
 * endpoint as the existing full-form flow. Rating is required, comment is
 * optional server-side, so the quick path is a valid submission.
 */
export default function QuickReview({ delivery, onSubmitted, onAddComment }) {
  const [hoverIdx, setHoverIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const handlePick = async (rating) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await submitReview(delivery.id, { rating, comment: '' });
      toast.success(`Thanks — you rated the driver ${rating} star${rating !== 1 ? 's' : ''}`, {
        action: onAddComment ? {
          label: 'Add a comment',
          onClick: () => onAddComment(delivery, rating),
        } : undefined,
      });
      onSubmitted && onSubmitted({ rating, comment: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg"
      data-testid={`quick-review-${delivery.id}`}
    >
      <span className="text-xs font-semibold text-yellow-800 uppercase tracking-wider">
        Rate driver:
      </span>
      <div
        className="inline-flex items-center gap-0.5"
        onMouseLeave={() => setHoverIdx(0)}
      >
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = hoverIdx >= n;
          return (
            <button
              key={n}
              type="button"
              disabled={submitting}
              aria-label={`Rate ${n} star${n !== 1 ? 's' : ''}`}
              onMouseEnter={() => setHoverIdx(n)}
              onFocus={() => setHoverIdx(n)}
              onClick={() => handlePick(n)}
              className="p-0.5 disabled:opacity-50 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-yellow-400 rounded"
              data-testid={`quick-review-star-${delivery.id}-${n}`}
            >
              <Star
                className={`w-5 h-5 ${filled ? 'fill-yellow-400 text-yellow-500' : 'text-yellow-400'}`}
                strokeWidth={1.5}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
